import React from 'react'

/**
 * The BrandLoader animation, sized for the inside of a dialog rather than the
 * whole screen: the Scale "S" mark breathing in a pulsing teal aura, a shimmer
 * clipped to the mark's silhouette, a short label and an indeterminate bar.
 *
 * Same approach as BrandLoader — inline styles + raw keyframes in a scoped
 * <style> block, because the Tailwind build does not reliably emit arbitrary
 * animation utilities. Keyframe names carry an `ibl-` prefix so they can never
 * collide with BrandLoader's `sm-` ones when both are mounted.
 */
const InlineBrandLoader = ({ label = 'Please wait…', sublabel = '' }) => (
    <div
        role="status"
        aria-live="polite"
        style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 280,
            padding: '24px 0',
        }}
    >
        <style>{`
            @keyframes ibl-breathe { 0%,100% { transform: scale(0.965); } 50% { transform: scale(1.05); } }
            @keyframes ibl-aura { 0%,100% { opacity: 0.28; transform: translate(-50%, -50%) scale(0.82); } 50% { opacity: 0.6; transform: translate(-50%, -50%) scale(1.18); } }
            @keyframes ibl-shine { 0% { background-position: 220% 0; } 100% { background-position: -120% 0; } }
            @keyframes ibl-bar { 0% { transform: translateX(-110%); } 100% { transform: translateX(420%); } }
            @keyframes ibl-rise { 0% { opacity: 0; transform: translateY(6px); } 100% { opacity: 1; transform: translateY(0); } }
        `}</style>

        {/* Mark + aura */}
        <div
            style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 84,
                height: 84,
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: 92,
                    height: 92,
                    borderRadius: '50%',
                    background:
                        'radial-gradient(circle, rgba(0,127,126,0.32) 0%, rgba(0,127,126,0) 67%)',
                    animation: 'ibl-aura 2.6s ease-in-out infinite',
                }}
            />
            <div
                style={{
                    position: 'relative',
                    width: 30,
                    height: 55,
                    animation: 'ibl-breathe 2.6s ease-in-out infinite',
                }}
            >
                <img
                    src="/scale-logo.svg"
                    alt=""
                    style={{ width: '100%', height: '100%', display: 'block' }}
                />
                {/* shimmer clipped to the mark's silhouette */}
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        WebkitMaskImage: 'url(/scale-logo.svg)',
                        maskImage: 'url(/scale-logo.svg)',
                        WebkitMaskSize: 'contain',
                        maskSize: 'contain',
                        WebkitMaskRepeat: 'no-repeat',
                        maskRepeat: 'no-repeat',
                        WebkitMaskPosition: 'center',
                        maskPosition: 'center',
                        background:
                            'linear-gradient(105deg, transparent 38%, rgba(255,255,255,0.92) 50%, transparent 62%)',
                        backgroundSize: '280% 100%',
                        animation: 'ibl-shine 2.2s ease-in-out infinite',
                    }}
                />
            </div>
        </div>

        <div
            style={{
                marginTop: 14,
                fontSize: 14,
                fontWeight: 600,
                color: '#0e2e2c',
                opacity: 0,
                animation: 'ibl-rise 0.5s ease-out 0.1s forwards',
            }}
        >
            {label}
        </div>
        {sublabel && (
            <div
                style={{
                    marginTop: 4,
                    fontSize: 12,
                    color: '#6b7280',
                    opacity: 0,
                    animation: 'ibl-rise 0.5s ease-out 0.2s forwards',
                }}
            >
                {sublabel}
            </div>
        )}

        {/* Indeterminate progress */}
        <div
            style={{
                marginTop: 14,
                position: 'relative',
                width: 120,
                height: 3,
                borderRadius: 999,
                background: 'rgba(0,127,126,0.14)',
                overflow: 'hidden',
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '24%',
                    height: '100%',
                    borderRadius: 999,
                    background: 'linear-gradient(90deg, #0a4f4e, #009b99)',
                    animation: 'ibl-bar 1.5s cubic-bezier(0.65, 0, 0.35, 1) infinite',
                }}
            />
        </div>
    </div>
)

export default InlineBrandLoader
